import { Component, Input, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import {
  Invoice,
  InvoiceService,
  isInvoiceTotalChanged,
  LoadInvoicesTotal,
  selectInvoice,
} from '@tokeet-frontend/invoices'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { BehaviorSubject, of, race, timer } from 'rxjs'
import { debounceTime, filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators'

@Component({
  selector: 'app-invoice-total',
  template: `
    <app-loading-value
      [value]="showCurrency ? (total | tokeetCurrency : invoice?.currency) : total"
      [loading]="isLoading"
    ></app-loading-value>
  `,
})
export class InvoiceTotalComponent extends Destroyable implements OnInit {
  private invoice$ = new BehaviorSubject<Invoice>(null)
  @Input() set invoice(invoice: Invoice) {
    this.invoice$.next(invoice)
  }
  get invoice() {
    return this.invoice$.value
  }

  @Input() showCurrency = true

  total: number
  isLoading = true

  constructor(private store: Store<any>, private invoiceService: InvoiceService) {
    super()
  }

  ngOnInit(): void {
    this.invoice$
      .pipe(
        filter((invoice): invoice is Invoice => !!invoice),
        pairwise(),
        filter(([prev, next]) => isInvoiceTotalChanged(prev?.invoiceItems, next?.invoiceItems)),
        untilDestroy(this)
      )
      .subscribe(([_, next]) => {
        this.store.dispatch(LoadInvoicesTotal({ invoiceIds: [next.id] }))
      })

    this.invoice$
      .pipe(
        filter((invoice): invoice is Invoice => !!invoice),
        debounceTime(250),
        tap(() => (this.isLoading = true)),
        switchMap((invoice) =>
          invoice.invoiceTotal != null
            ? of(invoice.invoiceTotal)
            : race(
                // Store invoices get totals batch-loaded (invoice/total/all) by InvoiceEffects —
                // wait for that instead of firing one invoice/total/{id} request per row
                this.store.pipe(
                  select(selectInvoice, { id: invoice.id }),
                  map((inv) => inv?.invoiceTotal),
                  filter((total) => total != null),
                  take(1)
                ),
                // Fallback for invoices that are not in the store and never get batch-loaded
                timer(8000).pipe(switchMap(() => this.invoiceService.total(invoice.id).pipe(map((d) => d.total))))
              )
        ),
        tap(() => (this.isLoading = false)),
        untilDestroy(this)
      )
      .subscribe((total) => {
        this.total = total || 0
      })
  }
}
